import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Tooltip,
  Badge,
  Button,
} from '@mui/material';
import {
  DataGrid,
  GridActionsCellItem,
  Toolbar,
  ToolbarButton,
  ColumnsPanelTrigger,
  FilterPanelTrigger,
} from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import FilterListIcon from '@mui/icons-material/FilterList';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { useDispatch, useSelector } from 'react-redux';

import { fetchSecrets, removeSecret } from '@/actions/projectSecrets';
import toNiceTimestamp from '@/util/toNiceTimestamp';
import { useUser } from '@/providers';
import ProjectSecretForm from '@/components/projects/Project/ProjectSecrets/ProjectSecretForm';

export default function ProjectSecrets({ project }) {
  const dispatch = useDispatch();
  const user = useUser();
  const project_secrets = useSelector((state) => state.projectSecrets);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({
    connector_id: false,
  });
  const [dialog_open, setDialogOpen] = useState(false);
  const [dialog_mode, setDialogMode] = useState('create');
  const [dialog_secret, setDialogSecret] = useState(null);

  useEffect(() => {
    dispatch(fetchSecrets(project.id));
  }, [dispatch, project.id]);

  const handleOpenCreate = useCallback(() => {
    setDialogMode('create');
    setDialogSecret(null);
    setDialogOpen(true);
  }, []);

  const handleOpenEdit = useCallback((row) => {
    setDialogMode('edit');
    setDialogSecret(row);
    setDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setDialogSecret(null);
  }, []);

  const deleteProjectSecret = useCallback(
    (project_secret) => async () => {
      try {
        await dispatch(
          removeSecret(project.id, project.id, project_secret.name),
        );
      } catch {
        // ErrorSnackBar via thunk
      }
    },
    [dispatch, project.id],
  );

  const formatDateColumn = useCallback((value) => {
    if (!value || (typeof value === 'string' && value.trim() === '')) {
      return '';
    }
    try {
      const date = new Date(value);
      if (isNaN(date.getTime())) {
        return '';
      }
      return toNiceTimestamp(value, true);
    } catch (error) {
      return '';
    }
  }, []);

  const columns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        type: 'string',
        flex: 1,
      },
      {
        field: 'created_by',
        headerName: 'Owner Name',
        type: 'string',
        flex: 0.5,
      },
      {
        field: 'createdAt',
        headerName: 'Created At',
        type: 'string',
        valueFormatter: formatDateColumn,
        flex: 0.5,
      },
      {
        field: 'updatedAt',
        headerName: 'Last Updated',
        type: 'string',
        valueFormatter: formatDateColumn,
        flex: 0.5,
      },
      {
        field: 'actions',
        headerName: 'Actions',
        type: 'actions',
        width: 100,
        getActions: (params) => {
          const actions = [];
          const can_edit =
            user?.id &&
            params.row.created_id != null &&
            user.id === params.row.created_id;
          if (can_edit) {
            actions.push(
              <GridActionsCellItem
                key='edit'
                icon={<EditIcon />}
                label='Edit'
                onClick={() => handleOpenEdit(params.row)}
                showInMenu
              />,
            );
          }
          actions.push(
            <GridActionsCellItem
              key='remove'
              icon={<DeleteIcon />}
              label='Remove'
              onClick={deleteProjectSecret(params.row)}
              showInMenu
            />,
          );
          return actions;
        },
      },
    ],
    [deleteProjectSecret, formatDateColumn, handleOpenEdit, user],
  );

  const ToolbarComponent = () => (
    <Toolbar
      sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}
    >
      <Stack direction='row' spacing={1}>
        <Button
          color='brand'
          variant='outlined'
          onClick={handleOpenCreate}
        >
          <Stack direction='row' spacing={1} alignItems='center'>
            <AddCircleOutlineIcon fontSize='small' />
            <Typography variant='button' display='block' gutterBottom>
              Add secret
            </Typography>
          </Stack>
        </Button>
      </Stack>
      <Stack direction='row' spacing={1}>
        <Tooltip title='Columns'>
          <ColumnsPanelTrigger render={<ToolbarButton />}>
            <ViewColumnIcon fontSize='small' color='brandBlack' />
          </ColumnsPanelTrigger>
        </Tooltip>
        <Tooltip title='Filters'>
          <FilterPanelTrigger
            render={(props, state) => (
              <ToolbarButton {...props}>
                <Badge badgeContent={state.filterCount} variant='dot'>
                  <FilterListIcon fontSize='small' color='brandBlack' />
                </Badge>
              </ToolbarButton>
            )}
          />
        </Tooltip>
      </Stack>
    </Toolbar>
  );

  return (
    <Accordion defaultExpanded>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls='panel1a-content'
        id='panel1a-header'
      >
        <Typography variant='h6' paragraph gutterBottom>
          Project Secrets
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <div style={{ width: '100%' }}>
          <div style={{ display: 'flex', height: '100%' }}>
            <div style={{ flexGrow: 1 }}>
              <DataGrid
                autoHeight
                getRowId={(row) => row.name}
                columns={columns}
                rows={project_secrets ?? []}
                slots={{
                  toolbar: ToolbarComponent,
                }}
                showToolbar
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={(newModel) =>
                  setColumnVisibilityModel(newModel)
                }
              />
            </div>
          </div>
        </div>
        <ProjectSecretForm
          open={dialog_open}
          onClose={handleCloseDialog}
          mode={dialog_mode}
          secret={dialog_secret}
          project={project}
        />
      </AccordionDetails>
    </Accordion>
  );
}
